import React from 'react';
import axios from 'axios';
import { API_URL, formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Edit, Trash2, Link2, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const deliverableStatuses = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' }
];

const invoiceStatuses = [
  { value: 'not_invoiced', label: 'Not Invoiced' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'cancelled', label: 'Cancelled' }
];

const paymentStatuses = [
  { value: 'pending', label: 'Pending' },
  { value: 'received', label: 'Received' },
  { value: 'partial', label: 'Partial' },
  { value: 'void', label: 'Void' }
];

const DELIVERABLE_COLORS = { completed: 'bg-green-500/20 text-green-600', in_progress: 'bg-blue-500/20 text-blue-600', on_hold: 'bg-amber-500/20 text-amber-600' };
const INVOICE_COLORS = { invoiced: 'bg-blue-500/20 text-blue-600', cancelled: 'bg-red-500/20 text-red-600' };
const getStatusColor = (status, colorMap) => colorMap[status] || 'bg-gray-500/20 text-gray-600';

export const BulkActionsBar = ({ selectedFeeItems, setSelectedFeeItems, onBulkStatusUpdate }) => {
  if (selectedFeeItems.length === 0) return null;
  return (
    <div className="flex items-center gap-4 p-3 bg-muted rounded-lg mb-4">
      <span className="text-sm font-medium">{selectedFeeItems.length} selected</span>
      <div className="flex gap-2">
        <Select onValueChange={(v) => onBulkStatusUpdate('invoice_status', v)}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Set Invoice Status" /></SelectTrigger>
          <SelectContent>{invoiceStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select onValueChange={(v) => onBulkStatusUpdate('payment_status', v)}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Set Payment Status" /></SelectTrigger>
          <SelectContent>{paymentStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select onValueChange={(v) => onBulkStatusUpdate('deliverable_status', v)}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Set Deliverable Status" /></SelectTrigger>
          <SelectContent>{deliverableStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Button variant="ghost" size="sm" onClick={() => setSelectedFeeItems([])}>Clear Selection</Button>
    </div>
  );
};

export const FeeStructureTable = ({
  filteredFeeStructure, selectedFeeItems, setSelectedFeeItems,
  feeSortField, toggleFeeSort, feeTotals, formatCurrency,
  onEdit, onDelete, onRefresh
}) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead className="w-10">
          <Checkbox
            checked={selectedFeeItems.length === filteredFeeStructure.length && filteredFeeStructure.length > 0}
            onCheckedChange={() => {
              setSelectedFeeItems(
                selectedFeeItems.length === filteredFeeStructure.length
                  ? []
                  : filteredFeeStructure.map(f => f.item_id)
              );
            }}
          />
        </TableHead>
        <TableHead className="cursor-pointer hover:bg-muted" onClick={() => toggleFeeSort('stage')}>
          <div className="flex items-center gap-1">Stage <ArrowUpDown className="h-3 w-3" /></div>
        </TableHead>
        <TableHead className="cursor-pointer hover:bg-muted" onClick={() => toggleFeeSort('deliverable')}>
          <div className="flex items-center gap-1">Deliverable <ArrowUpDown className="h-3 w-3" /></div>
        </TableHead>
        <TableHead className="text-right">%</TableHead>
        <TableHead className="text-right">Amount</TableHead>
        <TableHead>Billing Date</TableHead>
        <TableHead>Deliverable</TableHead>
        <TableHead>Invoice</TableHead>
        <TableHead>Payment</TableHead>
        <TableHead>Task</TableHead>
        <TableHead></TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {filteredFeeStructure.map((item) => (
        <TableRow key={item.item_id} className={selectedFeeItems.includes(item.item_id) ? 'bg-muted/50' : ''}>
          <TableCell>
            <Checkbox
              checked={selectedFeeItems.includes(item.item_id)}
              onCheckedChange={() => {
                setSelectedFeeItems(prev =>
                  prev.includes(item.item_id)
                    ? prev.filter(id => id !== item.item_id)
                    : [...prev, item.item_id]
                );
              }}
            />
          </TableCell>
          <TableCell className="font-medium">{item.stage}</TableCell>
          <TableCell>{item.deliverable}</TableCell>
          <TableCell className="text-right">{item.percentage}%</TableCell>
          <TableCell className="text-right font-semibold">{formatCurrency(item.amount)}</TableCell>
          <TableCell>{item.tentative_billing_date ? formatDate(item.tentative_billing_date) : '-'}</TableCell>
          <TableCell>
            <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(item.deliverable_status, DELIVERABLE_COLORS)}`}>
              {deliverableStatuses.find(s => s.value === item.deliverable_status)?.label}
            </span>
          </TableCell>
          <TableCell>
            <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(item.invoice_status, INVOICE_COLORS)}`}>
              {invoiceStatuses.find(s => s.value === item.invoice_status)?.label}
            </span>
          </TableCell>
          <TableCell>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              item.payment_status === 'received' ? 'bg-green-500/20 text-green-600' : 'bg-amber-500/20 text-amber-600'
            }`}>
              {paymentStatuses.find(s => s.value === item.payment_status)?.label}
            </span>
          </TableCell>
          <TableCell>
            {item.linked_task_id ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary" data-testid={`fee-task-link-${item.item_id}`}>
                <Link2 className="h-3 w-3" /> Linked
              </span>
            ) : (
              <Button
                size="sm" variant="ghost"
                className="h-7 text-xs text-muted-foreground hover:text-primary"
                onClick={async () => {
                  try {
                    await axios.post(`${API_URL}/fee-structure/${item.item_id}/link-task`, {}, { withCredentials: true });
                    toast.success('Task created and linked');
                    onRefresh();
                  } catch {
                    toast.error('Failed to link task');
                  }
                }}
                data-testid={`fee-link-btn-${item.item_id}`}
              >
                <Link2 className="h-3 w-3 mr-1" /> Link Task
              </Button>
            )}
          </TableCell>
          <TableCell>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => onEdit(item)}><Edit className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => onDelete(item.item_id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
      ))}
      <TableRow className="bg-muted/50 font-semibold">
        <TableCell colSpan={2}>Total</TableCell>
        <TableCell className="text-right">{feeTotals.percentage.toFixed(1)}%</TableCell>
        <TableCell className="text-right">{formatCurrency(feeTotals.amount)}</TableCell>
        <TableCell colSpan={6}></TableCell>
      </TableRow>
    </TableBody>
  </Table>
);
